import { useState, useEffect, useRef } from "react";
import { Box, Stack, Checkbox, FormControlLabel } from "@mui/material";
import { alpha } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import IconBtn from "components/lib-ui/IconBtn";
import DiagonalPatternBox from "../DiagonalPatternBox";
import LocationAutoComplete from "../dynamicInputs/LocationAutoComplete";

export const LocationField = ({
    fieldName,
    defaultValue,
    diffView = false,
    disabled = false,
    hookFormSetValue = () => {},
}) => {
    const theme = useTheme();
    const { control } = useForm();
    const locationValueRef = useRef(null);
    const data = useSelector(
        (state) => state.parsedProfileDataSlice.editableData,
    );
    const [isChecked, setIsChecked] = useState(() => !!defaultValue);
    const [isEditing, setIsEditing] = useState(() => !defaultValue);
    const [currentValue, setCurrentValue] = useState(defaultValue ?? null);

    const isEdited = currentValue !== defaultValue;

    const styles = {
        wrapper: {
            width: "100%",
            minHeight: "38px",
            display: "grid",
            gridTemplateColumns: diffView ? "1fr 38px 1fr" : "38px 1fr",
            padding: 0,
            ":hover [type='button']": {
                opacity: 1,
                pointerEvents: "auto",
                background: "white",
            },
        },
        text: {
            display: "inline",
            lineHeight: "inherit",
            wordBreak: "break-word",
            whiteSpace: "break-spaces",
        },
        disabledText: {
            color: theme.palette.error.main,
            background: alpha(theme.palette.error.main, 0.1),
            textDecoration: "line-through",
        },
        successText: {
            color: theme.palette.success.main,
            background: alpha(theme.palette.success.main, 0.2),
        },
    };

    const stateColor = isChecked
        ? theme.palette.success.main
        : theme.palette.error.main;
    const valueStateStyles = {
        background: alpha(stateColor, 0.1),
        "& .MuiInputBase-root": {
            background: alpha(stateColor, isChecked ? 0.2 : 0.1),
        },
        "& .MuiInputBase-input, & .MuiInputBase-input.Mui-disabled": {
            color: stateColor,
            WebkitTextFillColor: `${stateColor} !important`,
            ...(!isChecked && { textDecoration: "line-through" }),
        },
    };

    useEffect(() => {
        hookFormSetValue(fieldName, isChecked ? locationValueRef.current : "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isChecked]);

    const handleLocationChange = (_fieldName, newValue) => {
        locationValueRef.current = newValue;
        setCurrentValue(newValue);
        setIsEditing(false);
        if (isChecked) {
            hookFormSetValue(fieldName, newValue);
        } else {
            setIsChecked(true);
        }
    };

    const handleResetLocation = () => {
        locationValueRef.current = defaultValue ?? null;
        setCurrentValue(defaultValue ?? null);
        if (defaultValue) {
            hookFormSetValue(fieldName, defaultValue);
            setIsChecked(true);
            setIsEditing(false);
        } else {
            hookFormSetValue(fieldName, "");
            setIsChecked(false);
            setIsEditing(true);
        }
    };

    const existingLocationTitle =
        data?.existingUserData?.generalInfo?.location?.title;

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Stack
                component="div"
                direction="row"
                justifyContent="center"
                alignItems="center"
                sx={styles.wrapper}
            >
                {diffView &&
                    (existingLocationTitle ? (
                        <Box
                            sx={{
                                ml: 2,
                                ...styles.text,
                                ...(isChecked && styles.disabledText),
                                ...(!isChecked && styles.successText),
                            }}
                        >
                            {existingLocationTitle}
                        </Box>
                    ) : (
                        <DiagonalPatternBox />
                    ))}

                <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                        <Checkbox
                            checked={disabled ? true : !!isChecked}
                            onChange={() => setIsChecked((checked) => !checked)}
                            size="small"
                            disabled={disabled}
                        />
                    }
                />

                <Stack direction="row">
                    <Box
                        sx={{
                            flexGrow: 1,
                            ...valueStateStyles,
                        }}
                    >
                        <LocationAutoComplete
                            key={
                                currentValue?.id ??
                                currentValue?.title ??
                                "empty"
                            }
                            fieldName={fieldName}
                            textFieldVariant="standard"
                            textFieldLabel={null}
                            value={currentValue}
                            disabled={disabled}
                            open={isEditing}
                            emitFullValue
                            renderEditControl={() => null}
                            formControls={{
                                control,
                                setValue: handleLocationChange,
                            }}
                        />
                    </Box>

                    <Stack
                        pl={1}
                        spacing={1}
                        direction="row"
                        justifyContent="flex-end"
                        sx={{
                            position: "sticky",
                            top: "20px",
                            marginLeft: "auto",
                        }}
                    >
                        {isEdited && (
                            <IconBtn
                                icon={ClearOutlinedIcon}
                                onClick={handleResetLocation}
                                disabled={disabled}
                                iconStyles={{ color: "error.main" }}
                                sx={{
                                    opacity: 0,
                                    pointerEvents: "none",
                                    border: "unset",
                                    background: "white",
                                }}
                            />
                        )}
                        {!isEditing && (
                            <IconBtn
                                icon={EditOutlinedIcon}
                                onClick={() => setIsEditing(true)}
                                disabled={disabled}
                                sx={{
                                    opacity: 0,
                                    pointerEvents: "none",
                                    border: "unset",
                                    background: "white",
                                }}
                            />
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    );
};

export default LocationField;
